import React, { useEffect, useState } from "react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { useNavigate } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Mail, Lock, UserPlus, User } from "lucide-react";

function RegisterCanvas() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.lastName) {
      newErrors.lastName = "Please Enter Last Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (!isValidPassword) {
      newErrors.confirmPassword =
        "Confirm Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/users/dashboard");
      } else {
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <>
      <div className="custom-loginpage-wrapper">
        <Container fluid className="h-100 p-0">
          <Row className="h-100 g-0">
            <Col lg={6} className="d-none d-lg-block custom-loginpage-imagebox">
              <div className="login-image-overlay"></div>
            </Col>

            <Col
              lg={6}
              className="d-flex justify-content-center align-items-center p-4 p-md-5"
            >
              <div className="custom-loginpage-formbox">
                <h1 className="custom-loginpage-title mb-1">Login</h1>

                <p className="text-muted mb-5">
                  Enter your details to access your account
                </p>

                <Form method="post" onSubmit={handleSubmit}>
                  <div className="d-flex gap-3 register-flex">
                    <Form.Group className="mb-md-3" controlId="firstName">
                      <Form.Label className="custom-form-label">
                        First Name <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-group-custom">
                        <User size={18} className="input-icon" />
                        <Form.Control
                          type="text"
                          name="firstName"
                          placeholder="First Name"
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </div>
                      {errors && errors.firstName && (
                        <span className="text-danger custom-input-span-text">
                          {errors.firstName}
                        </span>
                      )}
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Label className="custom-form-label">
                        Last Name <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-group-custom">
                        <User size={18} className="input-icon" />
                        <Form.Control
                          type="text"
                          name="lastName"
                          placeholder="Last Name"
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </div>
                      {errors && errors.lastName && (
                        <span className="text-danger custom-input-span-text">
                          {errors.lastName}
                        </span>
                      )}
                    </Form.Group>
                  </div>
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label className="custom-form-label">
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group-custom">
                      <Mail size={18} className="input-icon" />
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors && errors.email && (
                      <span className="text-danger custom-input-span-text">
                        {errors.email}
                      </span>
                    )}
                  </Form.Group>

                  <div className="d-flex gap-3 register-flex">
                    <Form.Group className="mb-md-3" controlId="password">
                      <Form.Label className="custom-form-label">
                        Password <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-group-custom">
                        <Lock size={18} className="input-icon" />
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="Password"
                          value={formData.password}
                          onChange={handleChange}
                        />
                      </div>
                      {errors && errors.password && (
                        <span className="text-danger custom-input-span-text">
                          {errors.password}
                        </span>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="confirmPassword">
                      <Form.Label className="custom-form-label">
                        Confirm Password <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-group-custom">
                        <Lock size={18} className="input-icon" />
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm Password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                        />
                      </div>
                      {errors && errors.confirmPassword && (
                        <span className="text-danger custom-input-span-text">
                          {errors.confirmPassword}
                        </span>
                      )}
                    </Form.Group>
                  </div>
                  <div>
                    <Button
                      className="custom-registerpage-submitbtn w-100 py-3"
                      type="submit"
                    >
                      REGISTER
                    </Button>
                  </div>

                  <div className="text-center mt-3 pt-1 border-top">
                    <p className="small text-muted">
                      Have an account?
                      <Link
                        to="/users/login"
                        className="text-dark fw-bold ms-2"
                      >
                        Login
                      </Link>
                    </p>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default RegisterCanvas;
